import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useThemeColor } from '../hooks/useThemeColor'
import { useTranslation } from '../i18n/LanguageContext'
import { CheckCheck, FileText, RefreshCw, Settings, PlusCircle, Bell } from 'lucide-react'
import { BASE_SHARES_URL } from '../components/constants'
import { toast } from 'react-hot-toast'

// ── Type → icon/colour mapping ────────────────────────────────────────────────
const TYPE_META = {
  document: { icon: FileText,   iconBg: '#EFF6FF', iconColor: '#2563EB' },
  sync:     { icon: RefreshCw,  iconBg: '#FFF7ED', iconColor: '#EA580C' },
  system:   { icon: Settings,   iconBg: '#FAF5FF', iconColor: '#7C3AED' },
  general:  { icon: PlusCircle, iconBg: '#F0FDF4', iconColor: '#16A34A' },
}
const fallbackMeta = { icon: Bell, iconBg: '#F3F4F6', iconColor: '#6B7280' }

// ── Derive group + formatted time from ISO created_at ────────────────────────
const deriveGroupAndTime = (createdAt) => {
  const now   = new Date()
  const date  = new Date(createdAt)
  const diffMs = now - date
  const diffH  = diffMs / 3_600_000

  const todayStart     = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterdayStart = new Date(todayStart - 86_400_000)

  if (date >= todayStart) {
    const m = Math.floor(diffMs / 60_000)
    if (m < 1) return { group: 'today', timeKey: 'notifications.justNow', timeVars: null, time: null }
    if (m === 1) return { group: 'today', timeKey: 'notifications.minuteAgo', timeVars: null, time: null }
    if (m < 60) return { group: 'today', timeKey: 'notifications.minutesAgo', timeVars: { m }, time: null }
    const h = Math.floor(diffH)
    if (h === 1) return { group: 'today', timeKey: 'notifications.hourAgo', timeVars: null, time: null }
    return { group: 'today', timeKey: 'notifications.hoursAgo', timeVars: { h }, time: null }
  }
  if (date >= yesterdayStart) {
    const hh = String(date.getHours()).padStart(2, '0')
    const mm = String(date.getMinutes()).padStart(2, '0')
    return { group: 'yesterday', timeKey: 'notifications.yesterdayAt', timeVars: { time: `${hh}:${mm}` }, time: null }
  }
  const formatted = date.toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' })
  return { group: 'earlier', timeKey: null, timeVars: null, time: formatted }
}

// ── Transform raw API notification → UI shape ─────────────────────────────────
const transform = (n) => {
  const { group, timeKey, timeVars, time } = deriveGroupAndTime(n.created_at)
  const meta = TYPE_META[n.type] || fallbackMeta
  return {
    id:          n.id,
    group,
    isRead:      n.is_read,
    type:        n.type,
    title:       n.title,
    description: n.description,
    timeKey,
    timeVars,
    time,
    actionLabel: n.action_label || null,
    actionLink:  n.action_link  || null,
    icon:        meta.icon,
    iconBg:      meta.iconBg,
    iconColor:   meta.iconColor,
  }
}

// ─────────────────────────────────────────────────────────────────────────────

const Notifications = () => {
  const { aispaceColor } = useThemeColor()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [notifications, setNotifications] = useState([])
  const [activeTab, setActiveTab]         = useState('all')
  const [loading, setLoading]             = useState(true)
  const [error, setError]                 = useState(null)
  const [processingId, setProcessingId]   = useState(null)

  const token = localStorage.getItem('token')

  const fetchNotifications = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetch(`${BASE_SHARES_URL}/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error('fetch failed')
      const data = await res.json()
      const list = (data.notifications || []).map(transform)
      setNotifications(list)
      window.dispatchEvent(new CustomEvent('notifCountChange', { detail: { count: list.filter(n => !n.isRead).length } }))
    } catch {
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => { fetchNotifications() }, [fetchNotifications])

  const dispatchCount = (count) =>
    window.dispatchEvent(new CustomEvent('notifCountChange', { detail: { count } }))

  // ── mark-all-read ───────────────────────────────────────────────────────────
  const markAllRead = async () => {
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })))
    dispatchCount(0)
    try {
      await fetch(`${BASE_SHARES_URL}/notifications/mark-all-read`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
      })
    } catch { /* silent — optimistic update already applied */ }
  }

  // ── mark single read on click ──────────────────────────────────────────────
  const markOneRead = async (id) => {
    setNotifications(prev => {
      const updated = prev.map(n => n.id === id ? { ...n, isRead: true } : n)
      dispatchCount(updated.filter(n => !n.isRead).length)
      return updated
    })
    try {
      await fetch(`${BASE_SHARES_URL}/notifications/${id}/read`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
      })
    } catch { /* silent */ }
  }

  // ── click: mark read + navigate ───────────────────────────────────────────
  const handleNotifClick = async (notif) => {
    if (processingId === notif.id) return
    if (!notif.isRead) markOneRead(notif.id)
    if (!notif.actionLink) return

    if (notif.actionLink.startsWith('http')) {
      window.open(notif.actionLink, '_blank', 'noopener,noreferrer')
      return
    }

    if (notif.actionLink.startsWith('/shared/')) {
      const shareToken = notif.actionLink.split('/shared/')[1]
      setProcessingId(notif.id)
      try {
        const res = await fetch(`${BASE_SHARES_URL}/check/${shareToken}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (res.status === 403 || res.status === 401) {
          toast.error(t('share.accessRevoked'))
          return
        }
        if (res.status === 404) {
          toast.error(t('share.notFound'))
          return
        }
        if (!res.ok) {
          toast.error(t('share.accessRevoked'))
          return
        }
      } catch {
        toast.error(t('share.accessRevoked'))
        return
      } finally {
        setProcessingId(null)
      }
      navigate(notif.actionLink, { state: { mode: 'full' } })
      return
    }

    navigate(notif.actionLink)
  }

  // ── filtering / grouping ───────────────────────────────────────────────────
  const groupLabels = {
    today:     t('notifications.groupToday'),
    yesterday: t('notifications.groupYesterday'),
    earlier:   t('notifications.groupEarlier'),
  }

  const filtered = notifications.filter(n => {
    if (activeTab === 'unread') return !n.isRead
    if (activeTab === 'system') return n.type === 'system'
    return true
  })

  const unreadCount = notifications.filter(n => !n.isRead).length
  const systemCount = notifications.filter(n => n.type === 'system').length

  const totalPerGroup = {}
  notifications.forEach(n => { totalPerGroup[n.group] = (totalPerGroup[n.group] || 0) + 1 })

  const groups = ['today', 'yesterday', 'earlier']
    .map(g => ({ key: g, label: groupLabels[g], items: filtered.filter(n => n.group === g), totalCount: totalPerGroup[g] || 0 }))
    .filter(g => g.items.length > 0)

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <div className="h-screen flex flex-col w-full">
      <header className="px-10 h-[80px] flex items-center justify-between flex-shrink-0">
        <h1 className="text-[22px] font-bold text-[#111827]">{t('notifications.pageTitle')}</h1>
        <button
          onClick={markAllRead}
          className="flex items-center gap-2 px-4 py-2.5 text-[14px] font-medium text-[#374151] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] transition-colors"
        >
          <CheckCheck size={18} className="text-[#6B7280]" />
          <span>{t('notifications.markAllRead')}</span>
        </button>
      </header>

      {/* Sticky Tabs */}
      <div className="sticky top-0 z-10 bg-white px-6 md:px-10 pb-0">
        <p className="text-[14px] text-[#6B7280] mb-6">{t('notifications.subtitle')}</p>
        <div className="flex items-center gap-6 border-b border-[#E5E7EB]">
          {[
            { id: 'all',    label: t('notifications.tabAll'),    count: filtered.length },
            { id: 'unread', label: t('notifications.tabUnread'), count: unreadCount },
            { id: 'system', label: t('notifications.tabSystem'), count: systemCount },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-3 text-[14px] font-medium transition-colors relative flex items-center gap-2 ${
                activeTab !== tab.id ? 'text-[#6B7280] hover:text-[#374151]' : ''
              }`}
              style={activeTab === tab.id ? { color: aispaceColor } : {}}
            >
              {tab.label}
              <span
                className="text-[11px] font-semibold px-2 py-0.5 rounded-full"
                style={activeTab === tab.id
                  ? { backgroundColor: `${aispaceColor}1A`, color: aispaceColor }
                  : { backgroundColor: '#F3F4F6', color: '#6B7280' }}
              >
                {tab.count}
              </span>
              {activeTab === tab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-[2px] rounded-full" style={{ backgroundColor: aispaceColor }} />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-6 md:px-10 py-6">

        {/* Loading */}
        {loading && (
          <div className="flex justify-center items-center py-20">
            <svg className="animate-spin h-7 w-7" viewBox="0 0 24 24" style={{ color: aispaceColor }}>
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <p className="text-[14px] text-[#6B7280]">Benachrichtigungen konnten nicht geladen werden.</p>
            <button
              onClick={fetchNotifications}
              className="text-[13px] font-medium underline"
              style={{ color: aispaceColor }}
            >
              Erneut versuchen
            </button>
          </div>
        )}

        {/* Groups */}
        {!loading && !error && (
          groups.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-[#9CA3AF]">
              <CheckCheck size={48} className="mb-3 text-[#D1D5DB]" />
              <p className="text-[16px] font-medium text-[#111827]">{t('notifications.empty')}</p>
              <p className="text-[14px] text-[#6B7280] mt-1">{t('notifications.emptyHint')}</p>
            </div>
          ) : (
            <div className="space-y-6">
              {groups.map(group => (
                <div key={group.key}>
                  {/* Group header */}
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-[12px] font-semibold text-[#6B7280] tracking-wider">{group.label}</span>
                    <div className="flex-1 h-px bg-[#E5E7EB]" />
                    <span className="text-[12px] font-medium text-[#9CA3AF]">{group.totalCount}</span>
                  </div>

                  {/* Cards */}
                  <div className="space-y-3">
                    {group.items.map(notif => {
                      const IconComp = notif.icon
                      const timeText = notif.timeKey ? t(notif.timeKey, notif.timeVars) : notif.time
                      return (
                        <div
                          key={notif.id}
                          onClick={() => handleNotifClick(notif)}
                          className={`flex items-center gap-4 px-5 py-4 rounded-xl border border-[#EEEEEE] hover:shadow-sm transition-colors ${
                            processingId === notif.id ? 'opacity-60 cursor-wait' : 'cursor-pointer'
                          } ${notif.isRead ? 'bg-white' : 'bg-[#F5F5F5]'}`}
                        >
                          {/* Unread dot */}
                          <div className="w-3 flex-shrink-0 flex items-center justify-center">
                            {!notif.isRead && (
                              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: aispaceColor }} />
                            )}
                          </div>

                          {/* Icon */}
                          <div
                            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: notif.iconBg }}
                          >
                            <IconComp size={20} style={{ color: notif.iconColor }} />
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <p className="text-[14px] font-semibold text-[#111827]">{notif.title}</p>
                            <p className="text-[13px] text-[#6B7280] mt-0.5 leading-relaxed">{notif.description}</p>
                            <div className="flex items-center gap-3 mt-1.5">
                              <span className="text-[12px] text-[#9CA3AF]">{timeText}</span>
                              {notif.actionLabel && notif.actionLink && (
                                <span className="text-[12px] font-semibold underline underline-offset-2" style={{ color: aispaceColor }}>
                                  {notif.actionLabel}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  )
}

export default Notifications
